import { useState, useRef, useEffect } from 'react'
import { Menu as MuiMenu, MenuItem } from '@mui/material'
import AssociaMaterialeModal from '../../components/AssociaMaterialeModal'
import UploadMaterialModal from '../../components/UploadMaterialModal'
import AddTestModal from '../../components/AddTestModal'
import MaterialViewerModal from '../../components/MaterialViewerModal'
import { motion, AnimatePresence } from 'framer-motion'

interface MaterialeDidattico {
  id_corso_materiale_didattico: string;
  corso_id: string;
  materiale_didattico_id: string;
  sort_order: string;
  id_materiale_didattico: string;
  titolo: string;
  descrizione: string;
  durata: string;
  url: string;
  url_video: string | null;
  tipo_materiale: string;
}

interface MaterialeDidatticoSectionProps {
  isReadOnly?: boolean;
  data?: MaterialeDidattico[];
}

const MaterialeDidatticoSection = ({ isReadOnly = false, data = [] }: MaterialeDidatticoSectionProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [activeUploadModal, setActiveUploadModal] = useState<'video' | 'scorm' | 'pdf' | null>(null)
  const [isAddTestModalOpen, setIsAddTestModalOpen] = useState(false)
  const [addTestModalInitialTab, setAddTestModalInitialTab] = useState<number>(0)
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedMaterialId, setSelectedMaterialId] = useState<string | null>(null)
  const [showViewerModal, setShowViewerModal] = useState(false)
  const [selectedMaterialForView, setSelectedMaterialForView] = useState<MaterialeDidattico | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const uploadOptions = [
    { id: 'video', label: 'Video' },
    { id: 'scorm', label: 'SCORM' },
    { id: 'pdf', label: 'PDF' }
  ]

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleUploadOptionClick = (optionId: 'video' | 'scorm' | 'pdf') => {
    setActiveUploadModal(optionId)
    setIsDropdownOpen(false)
  }

  const closeUploadModal = () => {
    setActiveUploadModal(null)
  }

  const handleQuestionsPerTestClick = () => {
    setAddTestModalInitialTab(1)
    setIsAddTestModalOpen(true)
  }

  const handleAssignedTestsClick = () => {
    setAddTestModalInitialTab(2)
    setIsAddTestModalOpen(true)
  }

  const handleActionMenuOpen = (event: React.MouseEvent<HTMLElement>, materialId: string) => {
    setActionMenuAnchorEl(event.currentTarget)
    setSelectedMaterialId(materialId)
  }

  const handleActionMenuClose = () => {
    setActionMenuAnchorEl(null)
    setSelectedMaterialId(null)
  }

  const handleDeleteMaterial = () => {
    if (selectedMaterialId) {
      console.log('Eliminando materiale con ID:', selectedMaterialId)
      handleActionMenuClose()
    }
  }

  const handlePlayClick = (material: MaterialeDidattico) => {
    setSelectedMaterialForView(material)
    setShowViewerModal(true)
  }

  const closeViewerModal = () => {
    setShowViewerModal(false)
    setSelectedMaterialForView(null)
  }

  const handleClipboardClick = () => {
    setAddTestModalInitialTab(1)
    setIsAddTestModalOpen(true)
  }

  return (
    <div className="flex flex-col gap-1 w-full my-6">
      <span className="text-[20px] text-[#4F4F4F] font-bold mt-2">Materiale didattico</span>

      {/* Materials List */}
      <div className="mt-4 space-y-0">
        {data.length > 0 && data.map((material) => (
            <div key={material.id_corso_materiale_didattico} className="flex items-center justify-between py-2 border-b border-gray-200">
              {/* Description */}
              <div className="flex-1">
                <span className="text-sm text-[#4F4F4F] font-medium">{material.titolo}</span>
                <span className="text-sm text-gray-400 ml-2">({material.tipo_materiale})</span>
              </div>

              {/* Duration */}
              <div className="ml-5">
                <span className="text-sm text-[#4F4F4F]">Durata: {material.durata}</span>
              </div>

              {/* Action Icons */}
              <div className="flex items-center gap-2 ml-4">
                {!isReadOnly && (
                  <>
                    <span
                      className="p-1 hover:bg-gray-100 rounded transition-colors cursor-pointer"
                      onClick={() => handlePlayClick(material)}
                    >
                      <i className="ri-play-circle-line text-gray-500 text-xl"></i>
                    </span>
                    <span
                      className="p-1 hover:bg-gray-100 rounded transition-colors cursor-pointer"
                      onClick={handleClipboardClick}
                    >
                      <i className="ri-clipboard-line text-gray-500 text-xl"></i>
                    </span>
                  </>
                )}
                <span
                  className="p-1 hover:bg-gray-100 rounded transition-colors cursor-pointer"
                  onClick={(e) => handleActionMenuOpen(e, material.id_corso_materiale_didattico)}
                >
                  <i className="ri-more-2-fill text-gray-500 text-xl"></i>
                </span>
              </div>
            </div>
          ))
        }
      </div>

      {/* Action Buttons */}
      {!isReadOnly && (
        <div className="flex items-center gap-3 mt-2">
          <span
            onClick={() => setIsModalOpen(true)}
            className="bg-[#B80F15] text-white px-4 py-2 rounded text-sm font-semibold uppercase hover:bg-[#A00D13] transition-colors cursor-pointer"
          >
            + SCEGLI DA PIATTAFORMA
          </span>
          <div className="relative" ref={dropdownRef}>
            <span
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="bg-[#E3EBF7] text-[#285192] px-4 py-2 rounded text-sm font-semibold uppercase hover:bg-[#BDBDBD] transition-colors flex items-center gap-1 cursor-pointer"
            >
              + CARICA NUOVO MATERIALE
              <i className={`ri-arrow-down-s-fill text-[14px] transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
            </span>

            {/* Dropdown Menu */}
            <AnimatePresence>
              {isDropdownOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                  className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded shadow-lg z-50 min-w-[200px]"
                >
                  {uploadOptions.map((option) => (
                    <div
                      key={option.id}
                      onClick={() => handleUploadOptionClick(option.id as 'video' | 'scorm' | 'pdf')}
                      className="px-4 py-3 text-sm text-[#4F4F4F] hover:bg-gray-50 cursor-pointer transition-colors first:rounded-t last:rounded-b"
                    >
                      {option.label}
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      )}

      {/* Modals */}
      <AssociaMaterialeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />

      <UploadMaterialModal
        isOpen={activeUploadModal === 'video'}
        onClose={closeUploadModal}
        materialType="video"
      />

      <UploadMaterialModal
        isOpen={activeUploadModal === 'scorm'}
        onClose={closeUploadModal}
        materialType="scorm"
      />

      <UploadMaterialModal
        isOpen={activeUploadModal === 'pdf'}
        onClose={closeUploadModal}
        materialType="pdf"
      />

      <AddTestModal
        isOpen={isAddTestModalOpen}
        onClose={() => {
          setIsAddTestModalOpen(false)
          setAddTestModalInitialTab(0)
        }}
        initialTab={addTestModalInitialTab}
      />

      {/* Action Menu */}
      <MuiMenu
        anchorEl={actionMenuAnchorEl}
        open={Boolean(actionMenuAnchorEl)}
        onClose={handleActionMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 220,
            boxShadow: '1px 1px 15px 0px rgba(0, 0, 0, 0.1)',
            borderRadius: '8px',
          },
        }}
      >
        <MenuItem
          onClick={() => {
            handleQuestionsPerTestClick();
            handleActionMenuClose();
          }}
          sx={{ color: '#000000' }}
        >
          Domande per test
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleAssignedTestsClick();
            handleActionMenuClose();
          }}
          sx={{ color: '#000000' }}
        >
          Prerequisiti
        </MenuItem>
        {!isReadOnly && (
          <MenuItem onClick={handleDeleteMaterial} sx={{ color: '#000000' }}>
            Elimina
          </MenuItem>
        )}
      </MuiMenu>

      {/* Material Viewer Modal */}
      <MaterialViewerModal
        isOpen={showViewerModal}
        onClose={closeViewerModal}
        material={selectedMaterialForView}
      />
    </div>
  )
}

export default MaterialeDidatticoSection
